import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { Globe, Wallet } from "lucide-react";
import { useTickMarkets, fmt, fmtChange, mulberry32 } from "@/lib/market";
import { XMark } from "./Logo";

/* Neutral BTC candle series for the featured chart */
const CANDLES = (() => {
    const r = mulberry32(11);
    let v = 50;
    return Array.from({ length: 28 }, () => {
        const o = v;
        v = Math.max(14, Math.min(86, v + (r() - 0.44) * 10));
        const hi = Math.max(o, v) + r() * 6;
        const lo = Math.min(o, v) - r() * 6;
        return { o, c: v, hi, lo, up: v >= o };
    });
})();

const CandleChart = () => {
    const W = 460;
    const H = 150;
    const step = W / CANDLES.length;
    const line = CANDLES.map((c, i) => `${i ? "L" : "M"}${(i * step + step / 2).toFixed(1)},${(H - c.c).toFixed(1)}`).join(" ");
    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="h-full w-full" preserveAspectRatio="none" aria-hidden="true">
            {[0.2, 0.4, 0.6, 0.8].map((p) => (
                <line key={p} x1="0" x2={W} y1={H * p} y2={H * p} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
            ))}
            {CANDLES.map((c, i) => {
                const x = i * step + step / 2;
                const col = c.up ? "#10B981" : "#EF4444";
                return (
                    <g key={i}>
                        <line x1={x} x2={x} y1={H - c.hi} y2={H - c.lo} stroke={col} strokeWidth="0.7" opacity="0.8" />
                        <rect
                            x={x - step * 0.3}
                            width={step * 0.6}
                            y={H - Math.max(c.o, c.c)}
                            height={Math.max(1.5, Math.abs(c.o - c.c))}
                            fill={col}
                            opacity="0.7"
                            rx="0.5"
                        />
                    </g>
                );
            })}
            <motion.path
                d={line}
                fill="none"
                stroke="rgba(0,209,255,0.6)"
                strokeWidth="1.2"
                initial={{ pathLength: 0 }}
                whileInView={{ pathLength: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
            />
        </svg>
    );
};

const RAIL = ["grid", "candles", "coins", "wallet", "settings"];
const RAIL_ICONS = ["▦", "▤", "◈", "▭", "⚙"];
const LIST = ["BTC/USDT", "ETH/USDT", "BNB/USDT", "SOL/USDT", "XRP/USDT"];
const meta = (s) => s.split("/")[0];

const FloatingCard = ({ className, depth = 80, delay = 0, testId, children }) => (
    <motion.div
        data-testid={testId}
        className={`absolute z-30 hidden lg:block ${className}`}
        style={{ transform: `translateZ(${depth}px)` }}
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.8, delay }}
    >
        <div className="glass flex animate-float-y items-center gap-3 rounded-lg px-4 py-3" style={{ animationDelay: `${delay}s` }}>
            {children}
        </div>
    </motion.div>
);

/* Premium 3D terminal showcase — the TAVONX trading interface floating in space */
export default function PlatformShowcase() {
    const quotes = useTickMarkets();
    const btc = quotes["BTC/USDT"];
    const ref = useRef(null);
    const rxRaw = useMotionValue(0);
    const ryRaw = useMotionValue(0);
    const rx = useSpring(rxRaw, { stiffness: 45, damping: 14 });
    const ry = useSpring(ryRaw, { stiffness: 45, damping: 14 });

    const onMove = (e) => {
        const r = ref.current?.getBoundingClientRect();
        if (!r) return;
        ryRaw.set(((e.clientX - r.left) / r.width - 0.5) * 6);
        rxRaw.set(((e.clientY - r.top) / r.height - 0.5) * -5);
    };
    const onLeave = () => {
        rxRaw.set(0);
        ryRaw.set(0);
    };

    return (
        <section id="platform" data-testid="platform-showcase-section" className="relative overflow-hidden bg-base py-24 lg:py-32">
            {/* atmospheric background: glow + subtle grid + light trails + particles */}
            <div
                className="absolute inset-0 bg-grid opacity-30"
                style={{
                    maskImage: "radial-gradient(ellipse 72% 62% at 50% 46%, black 22%, transparent 78%)",
                    WebkitMaskImage: "radial-gradient(ellipse 72% 62% at 50% 46%, black 22%, transparent 78%)",
                }}
            />
            <div className="pointer-events-none absolute left-1/2 top-[54%] h-[62vh] w-[82vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse,rgba(22,139,255,0.11),rgba(0,209,255,0.05)_45%,transparent_72%)]" />
            <div className="pointer-events-none absolute left-[7%] top-[20%] h-px w-56 -rotate-12 bg-gradient-to-r from-transparent via-cyber/25 to-transparent" />
            <div className="pointer-events-none absolute right-[8%] bottom-[18%] h-px w-64 rotate-6 bg-gradient-to-r from-transparent via-roy/30 to-transparent" />
            {[
                "left-[14%] top-[30%]", "left-[22%] bottom-[24%]", "right-[16%] top-[24%]",
                "right-[24%] bottom-[30%]", "left-[45%] top-[14%]", "right-[40%] bottom-[14%]",
            ].map((p, i) => (
                <span
                    key={i}
                    className={`pointer-events-none absolute h-1 w-1 animate-float-y rounded-full bg-cyan-300/50 ${p}`}
                    style={{ animationDelay: `${i * 1.1}s` }}
                />
            ))}

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <div className="mx-auto max-w-3xl text-center">
                    <div className="mb-6 flex items-center justify-center gap-3">
                        <span className="h-px w-9 bg-gradient-to-r from-transparent to-cyber" />
                        <span className="font-mono text-[11px] uppercase tracking-[0.32em] text-cyber">
                            The Tavonx Terminal
                        </span>
                        <span className="h-px w-9 bg-gradient-to-l from-transparent to-cyber" />
                    </div>
                    <h2 className="font-grotesk text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                        Your markets.
                        <br />
                        <span className="text-grad-blue">Wherever you are.</span>
                    </h2>
                    <p className="mx-auto mt-5 max-w-md text-base leading-relaxed text-slate-400">
                        A trading experience designed for speed, clarity and control.
                    </p>
                </div>

                <div
                    ref={ref}
                    onMouseMove={onMove}
                    onMouseLeave={onLeave}
                    className="relative mx-auto mt-20 max-w-5xl [perspective:2200px]"
                >
                    {/* soft shadow beneath the floating terminal */}
                    <div className="pointer-events-none absolute inset-x-20 -bottom-16 h-28 rounded-full bg-black/75 blur-2xl" />

                    <motion.div
                        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
                        className="relative"
                    >
                        <div style={{ transform: "rotateX(5deg)", transformStyle: "preserve-3d" }}>
                            <motion.div
                                initial={{ opacity: 0, y: 60 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.25 }}
                                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <div className="animate-float-y" style={{ transformStyle: "preserve-3d" }}>
                                    {/* the floating terminal */}
                                    <div
                                        data-testid="showcase-desktop-mockup"
                                        className="relative overflow-hidden rounded-xl border border-white/10 bg-[linear-gradient(180deg,#0D1424_0%,#0A0F1D_100%)] shadow-[0_60px_160px_rgba(0,0,0,0.72)]"
                                    >
                                        {/* thin electric-blue edge lighting */}
                                        <span className="absolute inset-x-0 top-0 z-20 h-px bg-gradient-to-r from-transparent via-cyber/80 to-transparent" />
                                        <span className="absolute inset-x-0 bottom-0 z-20 h-px bg-gradient-to-r from-transparent via-roy/60 to-transparent" />
                                        {/* slow light pass across the edge */}
                                        <motion.div
                                            className="pointer-events-none absolute inset-y-0 z-20 w-1/4 -skew-x-12 bg-gradient-to-r from-transparent via-cyan-300/[0.06] to-transparent"
                                            animate={{ x: ["-140%", "460%"] }}
                                            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", repeatDelay: 2.5 }}
                                        />

                                        {/* terminal header */}
                                        <div className="flex items-center justify-between border-b border-white/[0.06] bg-void/50 px-5 py-3">
                                            <div className="flex items-center gap-2.5">
                                                <XMark className="h-4 w-auto" />
                                                <span className="font-grotesk text-[11px] font-bold uppercase tracking-[0.26em] text-white">
                                                    Tavonx <span className="text-cyber">Pro</span>
                                                </span>
                                            </div>
                                            <span className="rounded-full border border-cyber/25 bg-cyber/[0.06] px-2.5 py-0.5 font-mono text-[8px] uppercase tracking-[0.2em] text-cyan-300">
                                                Simulated · Demo data
                                            </span>
                                        </div>

                                        <div className="grid sm:grid-cols-[44px_1fr] lg:grid-cols-[44px_172px_1fr_208px]">
                                            {/* rail */}
                                            <div className="hidden flex-col items-center gap-1 border-r border-white/[0.06] py-4 sm:flex">
                                                {RAIL.map((r, i) => (
                                                    <span
                                                        key={r}
                                                        className={`flex h-8 w-8 items-center justify-center rounded text-[11px] ${
                                                            i === 2 ? "bg-cyber/15 text-cyber" : "text-slate-600"
                                                        }`}
                                                    >
                                                        {RAIL_ICONS[i]}
                                                    </span>
                                                ))}
                                            </div>

                                            {/* market list */}
                                            <div
                                                data-testid="showcase-market-list"
                                                className="hidden border-r border-white/[0.06] py-4 lg:block"
                                            >
                                                <div className="px-3 pb-2 font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500">
                                                    Markets
                                                </div>
                                                {LIST.map((s) => {
                                                    const q = quotes[s];
                                                    const featured = s === "BTC/USDT";
                                                    return (
                                                        <div
                                                            key={s}
                                                            className={`flex items-center justify-between px-3 py-2 ${
                                                                featured
                                                                    ? "border-l border-cyber bg-cyber/[0.07]"
                                                                    : "border-l border-transparent"
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-1.5">
                                                                <span className="h-4 w-4 text-center font-mono text-[9px] leading-4 text-slate-600">
                                                                    {meta(s).slice(0, 1)}
                                                                </span>
                                                                <div>
                                                                    <div className={`font-mono text-[10px] leading-none ${featured ? "text-white" : "text-slate-300"}`}>
                                                                        {meta(s)}
                                                                    </div>
                                                                    <div className="mt-0.5 font-mono text-[8px] text-slate-600">USDT</div>
                                                                </div>
                                                            </div>
                                                            <div className="text-right">
                                                                <div className="font-mono text-[9px] tabular-nums text-slate-200">
                                                                    ${fmt(q.price, q.price > 100 ? 1 : 4)}
                                                                </div>
                                                                <div className={`font-mono text-[8px] ${q.change >= 0 ? "text-bull" : "text-bear"}`}>
                                                                    {fmtChange(q.change)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {/* featured TNX chart + portfolio */}
                                            <div className="p-4">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="flex h-5 w-5 items-center justify-center rounded-full border border-white/15 bg-white/[0.04] font-mono text-[8px] text-cyber">B</span>
                                                        <span className="font-grotesk text-sm font-semibold text-white">
                                                            BTC<span className="text-slate-500">/USDT</span>
                                                        </span>
                                                        <span className="font-mono text-sm text-bull">+2.34%</span>
                                                    </div>
                                                    <span className="font-mono text-xs tabular-nums text-slate-300">
                                                        ${fmt(btc.price)}
                                                    </span>
                                </div>
                                                <div className="mt-3 h-36 overflow-hidden rounded border border-white/[0.06] bg-void/50 sm:h-44">
                                                    <CandleChart />
                                                </div>
                                                <div
                                                    data-testid="showcase-portfolio"
                                                    className="mt-3 rounded border border-white/[0.06] bg-white/[0.02] px-3 py-2.5"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-mono text-[8px] uppercase tracking-[0.22em] text-slate-500">
                                                            Portfolio value
                                                        </span>
                                                        <span className="font-mono text-[10px] text-slate-500">DEMO</span>
                                                    </div>
                                                    <div className="mt-1 flex items-baseline justify-between">
                                                        <span className="font-mono text-sm font-medium text-white">$12,482.66</span>
                                                        <span className="font-mono text-[10px] text-bull">+4.21%</span>
                                                    </div>
                                                    <div className="mt-2 flex h-1 w-full gap-0.5 overflow-hidden rounded">
                                                        <span className="h-full w-[45%] bg-cyber/80" />
                                                        <span className="h-full w-[30%] bg-electric/80" />
                                                        <span className="h-full w-[25%] bg-vio/70" />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* order panel */}
                                            <div
                                                data-testid="showcase-order-panel"
                                                className="hidden border-l border-white/[0.06] p-3 lg:block"
                                            >
                                                <div className="grid grid-cols-2 gap-1">
                                                    <span className="rounded bg-bull/15 py-1.5 text-center font-mono text-[9px] uppercase tracking-[0.16em] text-bull">
                                                        Buy
                                                    </span>
                                                    <span className="rounded py-1.5 text-center font-mono text-[9px] uppercase tracking-[0.16em] text-slate-500">
                                                        Sell
                                                    </span>
                                                </div>
                                                <div className="mt-3 space-y-2">
                                                    <div className="rounded border border-white/[0.07] px-2.5 py-2">
                                                        <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-600">Price</div>
                                                        <div className="font-mono text-[11px] tabular-nums text-white">{fmt(btc.price, 2)}</div>
                                                    </div>
                                                    <div className="rounded border border-white/[0.07] px-2.5 py-2">
                                                        <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-600">Amount</div>
                                                        <div className="font-mono text-[11px] text-white">0.1500 BTC</div>
                                                    </div>
                                                    <div className="flex justify-between px-1 font-mono text-[8px] text-slate-500">
                                                        <span>Est. cost</span>
                                                        <span className="text-slate-300">≈ ${fmt(btc.price * 0.15, 2)}</span>
                                                    </div>
                                                    <div className="rounded bg-gradient-to-r from-electric to-cyber py-2 text-center font-grotesk text-[10px] font-semibold uppercase tracking-[0.18em] text-void">
                                                        Buy BTC
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                            {/* floating data cards — outside the terminal */}
                            <FloatingCard testId="showcase-card-tvx" className="-top-9 right-8" depth={110} delay={0.5}>
                                <img
                                    src={`${process.env.PUBLIC_URL}/brand/tvx-icon.png`}
                                    alt="TNX"
                                    loading="lazy"
                                    draggable={false}
                                    className="h-9 w-9 select-none"
                                />
                                <div className="leading-tight">
                                    <div className="font-mono text-[10px] text-slate-300">TNX · Utility Token</div>
                                    <div className="font-mono text-sm font-medium text-white">Genesis · 28 Oct 2026</div>
                                </div>
                            </FloatingCard>

                            <FloatingCard testId="showcase-card-markets" className="-top-9 left-8" depth={70} delay={1.4}>
                                <Globe className="h-5 w-5 text-cyber" strokeWidth={1.5} />
                                <div className="leading-tight">
                                    <div className="font-grotesk text-xs font-semibold uppercase tracking-[0.16em] text-white">Markets</div>
                                    <div className="mt-0.5 font-mono text-[9px] tracking-[0.14em] text-slate-500">24/7 · GLOBAL ACCESS</div>
                                </div>
                            </FloatingCard>

                            <FloatingCard testId="showcase-card-portfolio" className="-bottom-10 right-8" depth={60} delay={2.3}>
                                <Wallet className="h-5 w-5 text-cyber" strokeWidth={1.5} />
                                <div className="leading-tight">
                                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">Portfolio</div>
                                    <div className="font-mono text-sm font-medium text-white">
                                        $12,482.66 <span className="ml-1 text-[9px] tracking-[0.14em] text-slate-500">DEMO</span>
                                    </div>
                                </div>
                            </FloatingCard>
                        </div>
                    </motion.div>
                </div>

                <p className="mt-24 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
                    Interface shown for illustration — landing page only, no live trading.
                </p>
            </div>
        </section>
    );
}
